import React, { ReactNode, useState } from "react";
import {
    faCircleInfo,
    faCode,
    faPaperPlane,
    faToolbox,
    faWandMagicSparkles,
    IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { ToolbarPillButton } from "../../../../../../shared/components/redesigned/DocumentViewer/Toolbar";
import SidePanel from "../../../../../../shared/components/redesigned/SidePanel";
import useTranslation from "../../../../../../shared/hooks/useTranslation";
import useUrlFlag from "../../../../../../shared/hooks/useUrlFlag";
import {
    useProcessUnitMutation,
    useRequeueUnitMutation,
    useRetryAutoBookingMutation,
    Unit,
} from "../../../../../../services/management/units/api";
import contract from "../../../../../../shared/components/AnnotationForm/contract";
import { notify } from "../../../../../../shared/redux/actions/notifications";
import { MetadataUnit } from "../../../../../../services/supervising/metadata/types";
import { ProcessedDataResponse } from "../../../../../../services/supervising/processedData/types";
import { SubmissionsResponse } from "../../../../../../services/supervising/submissions/types";
import ProcessedDataView from "../ProcessedDataView";
import SubmissionsView from "../SubmissionsView";

interface AnnotationFormValues {
    content?: string;
}

interface ApiFailure {
    status?: number | string;
    error?: string;
    data?: { errors?: { detail?: string }[] };
}

type TabKey = "overview" | "metadata" | "processed" | "submissions";

export default function useUnitToolbar(
    unit: Unit | undefined,
    metadata: MetadataUnit[] | undefined,
    processed: ProcessedDataResponse | undefined,
    processedLoading: boolean,
    submissions: SubmissionsResponse | undefined,
    submissionsLoading: boolean,
): { button: ReactNode; panel: ReactNode } {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<AnnotationFormValues>({ resolver: yupResolver(contract) });
    const [open, setOpen] = useUrlFlag("tools", "open");
    const [tab, setTab] = useState<TabKey>("overview");

    const [process] = useProcessUnitMutation();
    const [requeue] = useRequeueUnitMutation();
    const [retryAutoBooking, { isLoading: retrying }] = useRetryAutoBookingMutation();

    if (!unit) return { button: null, panel: null };

    const close = () => setOpen(false);
    const toggle = () => setOpen((prev) => !prev);

    const notifyFailure = ({ status, error, data }: ApiFailure) => {
        dispatch(notify({ text: data?.errors?.[0]?.detail ?? `${status} - ${error}` }));
    };

    const handlerBuilder =
        (fn: typeof process | typeof requeue) => async (data: AnnotationFormValues) => {
            try {
                await fn({ unitId: unit.id, reason: data?.content || "" }).unwrap();
                reset();
                close();
            } catch (e) {
                notifyFailure(e);
            }
        };

    const retry = async () => {
        try {
            const retried = await retryAutoBooking(unit.id).unwrap();
            if (retried.status === "skipped") {
                dispatch(notify({ text: t("unit_toolbar.retry_auto_booking.skipped_again") }));
            } else {
                close();
            }
        } catch (e) {
            notifyFailure(e);
        }
    };

    const button = (
        <ToolbarPillButton
            icon={faToolbox}
            title={t("unit_toolbar.buttons.tools.title")}
            onClick={toggle}
            variant="accent"
        />
    );

    const panel = open ? (
        <SidePanel
            title={`${unit.account.code} — Dokument ${unit.id} (AID) ${unit.documentId} (UOLU)`}
            onClose={close}
        >
            <TabBar active={tab} onChange={setTab} t={t} />
            {tab === "overview" && (
                <>
                    <dl className="grid grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-y-2 text-sm">
                        <dt className="text-ink-muted">{t("attributes.account.code")}</dt>
                        <dd className="text-ink">{unit.account.code}</dd>
                        <dt className="text-ink-muted">ARNOLD ID</dt>
                        <dd className="text-ink">{unit.id}</dd>
                        <dt className="text-ink-muted">UOLU ID</dt>
                        <dd className="text-ink">{unit.documentId}</dd>
                        <dt className="text-ink-muted">HARVY ID</dt>
                        <dd className="text-xs text-ink">{unit.harvyId}</dd>
                        <dt className="text-ink-muted">Formát</dt>
                        <dd className="text-ink">{unit.format.toUpperCase()}</dd>
                        <dt className="text-ink-muted">Stav</dt>
                        <dd className="text-ink">
                            {t(`attributes.data_entering.units.statuses.${unit.status}`)}
                        </dd>
                    </dl>
                    {unit.status == "skipped" && unit.autoBookable && (
                        <div className="mt-6 flex justify-end border-t border-border pt-4">
                            <button
                                type="button"
                                onClick={retry}
                                disabled={retrying}
                                className="rounded-md border border-accent bg-surface px-3 py-2 text-sm font-medium text-accent hover:bg-surface-3 disabled:opacity-50"
                            >
                                {t("unit_toolbar.retry_auto_booking.button")}
                            </button>
                        </div>
                    )}
                    {unit.status == "skipped" && (
                        <form className="mt-6 border-t border-border pt-4">
                            <label
                                htmlFor="content"
                                className="mb-1 block text-xs font-medium text-ink-muted"
                            >
                                {t("attributes.annotation.content")}
                            </label>
                            <textarea
                                id="content"
                                {...register("content")}
                                rows={5}
                                autoFocus
                                autoComplete="off"
                                className={[
                                    "w-full rounded-md border bg-surface px-3 py-2 text-sm text-ink",
                                    "focus:outline-none focus:ring-2 focus:ring-accent/40",
                                    errors.content ? "border-danger" : "border-border",
                                ].join(" ")}
                            />
                            {errors.content && (
                                <p className="mt-1 text-xs text-danger">
                                    {t(`errors.messages.${errors.content.type}`)}
                                </p>
                            )}
                            <div className="mt-4 flex justify-end gap-2">
                                {!unit.autoBookable && (
                                    <button
                                        type="button"
                                        onClick={handleSubmit(handlerBuilder(requeue))}
                                        disabled={isSubmitting}
                                        className="rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium text-ink hover:bg-surface-3 disabled:opacity-50"
                                    >
                                        {t(
                                            "resources.skipped_uploaded_documents.actions.resolve.reenque",
                                        )}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleSubmit(handlerBuilder(process))}
                                    disabled={isSubmitting}
                                    className="rounded-md bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
                                >
                                    {t(
                                        "resources.skipped_uploaded_documents.actions.resolve.process",
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </>
            )}
            {tab === "processed" && (
                <ProcessedDataView payload={processed} isLoading={processedLoading} />
            )}
            {tab === "submissions" && (
                <SubmissionsView submissions={submissions} isLoading={submissionsLoading} />
            )}
            {tab === "metadata" && (
                <>
                    {metadata?.length ? (
                        metadata.map((d) => (
                            <div key={d.sourceType} className="mb-4">
                                <div className="mb-1 text-xs font-medium text-ink-muted">
                                    {d.sourceType}
                                </div>
                                <pre className="max-h-96 overflow-auto rounded-md bg-surface-2 p-3 text-xs text-ink">
                                    {JSON.stringify(d.data, null, 2)}
                                </pre>
                            </div>
                        ))
                    ) : (
                        <p className="text-xs italic text-ink-muted">
                            {t("unit_toolbar.processed_data.empty")}
                        </p>
                    )}
                </>
            )}
        </SidePanel>
    ) : null;

    return { button, panel };
}

function TabBar({
    active,
    onChange,
    t,
}: {
    active: TabKey;
    onChange: (k: TabKey) => void;
    t: (key: string) => string;
}) {
    const tabs: { key: TabKey; label: string; icon: IconDefinition }[] = [
        { key: "overview", label: t("unit_toolbar.tabs.overview"), icon: faCircleInfo },
        { key: "metadata", label: t("unit_toolbar.tabs.metadata"), icon: faCode },
        { key: "processed", label: t("unit_toolbar.tabs.processed"), icon: faWandMagicSparkles },
        { key: "submissions", label: t("unit_toolbar.tabs.submissions"), icon: faPaperPlane },
    ];

    return (
        <div className="mb-4 flex gap-1 rounded-md bg-surface-2 p-1">
            {tabs.map((entry) => (
                <button
                    key={entry.key}
                    type="button"
                    onClick={() => onChange(entry.key)}
                    className={[
                        "flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition",
                        active === entry.key
                            ? "bg-surface text-ink shadow-sm"
                            : "text-ink-muted hover:text-ink",
                    ].join(" ")}
                >
                    <FontAwesomeIcon icon={entry.icon} className="text-[11px]" />
                    {entry.label}
                </button>
            ))}
        </div>
    );
}
